import { PolygonFeatureLabel3D } from '../procedural/PolygonFeatureLabel3D'
import {
  SurfaceLineFeature3D,
  SurfaceLineSelectionType,
} from '../procedural/SurfaceLineFeature3D'
import {
  SurfaceMeshFeature3D,
  SurfaceMeshSelectionType,
} from '../procedural/SurfaceMeshFeature3D'
import { useSurfaceProps } from '@asset-viz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoComponentPolygon,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoShipDeckCoaming,
} from 'types'

/**
 * @param model
 * @constructor
 */
export const DeckCoamingFeature3D: React.FC<{
  model: GeckoShipDeckCoaming
}> = ({ model }) => {
  const { bounds, wrapMode } = useSurfaceProps()

  const coords = model.coordinates
  const boundingPaths = React.useMemo(() => {
    const paths = []

    for (let i = 1; i < coords.length; i++) {
      // for every set of two coordinates, perform math to find slope
      // find points of the bounding box of lineThickness
      const pointA = coords[i - 1]
      const pointB = coords[i]
      const directionVector = [
        coords[i].x.minus(coords[i - 1].x),
        coords[i].y.minus(coords[i - 1].y),
      ]
      const perpendicularVector = [directionVector[1].neg(), directionVector[0]]

      //normalize perpendicular vector
      const squaredlengthOfN = perpendicularVector[0]
        .scale(perpendicularVector[0].toNumber(GeckoMeasurementUnit.Meters))
        .plus(
          perpendicularVector[1].scale(
            perpendicularVector[1].toNumber(GeckoMeasurementUnit.Meters)
          )
        )

      const lengthOfN = Math.sqrt(
        squaredlengthOfN.toNumber(GeckoMeasurementUnit.Meters)
      )
      const normalizedPerp = [
        perpendicularVector[0].toNumber(GeckoMeasurementUnit.Meters) /
          lengthOfN,
        perpendicularVector[1].toNumber(GeckoMeasurementUnit.Meters) /
          lengthOfN,
      ]

      // get thickness offset
      const offsetVector = normalizedPerp.map(
        (number: number) => (number *= model.lineThickness / 2)
      )

      // generate new points
      const p1 = {
        x: GeckoMeasurement.CreateFromValue(
          pointA.x.toNumber(GeckoMeasurementUnit.Meters) + offsetVector[0],
          GeckoMeasurementUnit.Meters
        ),
        y: GeckoMeasurement.CreateFromValue(
          pointA.y.toNumber(GeckoMeasurementUnit.Meters) + offsetVector[1],
          GeckoMeasurementUnit.Meters
        ),
      }
      const p2 = {
        x: GeckoMeasurement.CreateFromValue(
          pointA.x.toNumber(GeckoMeasurementUnit.Meters) - offsetVector[0],
          GeckoMeasurementUnit.Meters
        ),
        y: GeckoMeasurement.CreateFromValue(
          pointA.y.toNumber(GeckoMeasurementUnit.Meters) - offsetVector[1],
          GeckoMeasurementUnit.Meters
        ),
      }
      const p3 = {
        x: GeckoMeasurement.CreateFromValue(
          pointB.x.toNumber(GeckoMeasurementUnit.Meters) - offsetVector[0],
          GeckoMeasurementUnit.Meters
        ),
        y: GeckoMeasurement.CreateFromValue(
          pointB.y.toNumber(GeckoMeasurementUnit.Meters) - offsetVector[1],
          GeckoMeasurementUnit.Meters
        ),
      }
      const p4 = {
        x: GeckoMeasurement.CreateFromValue(
          pointB.x.toNumber(GeckoMeasurementUnit.Meters) + offsetVector[0],
          GeckoMeasurementUnit.Meters
        ),
        y: GeckoMeasurement.CreateFromValue(
          pointB.y.toNumber(GeckoMeasurementUnit.Meters) + offsetVector[1],
          GeckoMeasurementUnit.Meters
        ),
      }
      paths.push(
        GeckoComponentPolygon.CreateFromPolygonalPlate(
          [p1, p2, p3, p4],
          bounds,
          wrapMode
        )
      )
    }
    return paths
  }, [bounds, coords, model.lineThickness, wrapMode])

  return (
    <>
      {boundingPaths.map((boundingPath) => (
        <>
          <PolygonFeatureLabel3D feature={model} polygonPath={boundingPath} />
          <SurfaceLineFeature3D
            path={boundingPath}
            featureModel={model}
            lineSelectionType={SurfaceLineSelectionType.SelectableFeatureLine}
          />
          <SurfaceMeshFeature3D
            path={boundingPath}
            featureModel={model}
            selectionType={SurfaceMeshSelectionType.AssetModelEditorSelectable}
            colorMesh={true}
            fillColor={'purple'}
          />
        </>
      ))}
    </>
  )
}
